'use client';

import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface SearchBarProps {
  placeholder?: string;
  className?: string;
  onSearch?: (query: string) => void;
  autoFocus?: boolean;
  showSuggestions?: boolean;
}

interface SearchSuggestion {
  id: string;
  text: string;
  category?: string;
}

const SearchBar = ({ 
  placeholder = 'Search products...', 
  className = '',
  onSearch,
  autoFocus = false,
  showSuggestions = true
}: SearchBarProps) => {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const mockSuggestions: SearchSuggestion[] = [
    { id: '1', text: 'Digital Calipers', category: 'Measurement Tools' },
    { id: '2', text: 'Micrometers', category: 'Precision Instruments' },
    { id: '3', text: 'Dial Indicators', category: 'Gauges' },
    { id: '4', text: 'Laser Distance Meters', category: 'Electronic Tools' },
    { id: '5', text: 'Torque Wrenches', category: 'Calibration Tools' },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (query.trim() && showSuggestions) {
      const filtered = mockSuggestions.filter(suggestion =>
        suggestion.text.toLowerCase().includes(query.toLowerCase())
      );
      setSuggestions(filtered);
    } else {
      setSuggestions([]);
    }
  }, [query, showSuggestions]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      if (onSearch) {
        onSearch(query);
      } else {
        router.push(`/product-catalog?search=${encodeURIComponent(query)}`);
      }
      setIsFocused(false);
      inputRef.current?.blur();
    }
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    setQuery(suggestion.text);
    if (onSearch) {
      onSearch(suggestion.text);
    } else {
      router.push(`/product-catalog?search=${encodeURIComponent(suggestion.text)}`);
    }
    setIsFocused(false);
  };

  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    inputRef.current?.focus();
  };

  return (
    <div data-component-id="src/components/common/SearchBar.tsx:95:4" data-component-path="src/components/common/SearchBar.tsx" data-component-line="95" data-component-end-line="158" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" ref={containerRef} className={`relative ${className}`}>
      <form data-component-id="src/components/common/SearchBar.tsx:96:6" data-component-path="src/components/common/SearchBar.tsx" data-component-line="96" data-component-end-line="125" data-component-file="SearchBar.tsx" data-component-name="form" data-component-content="%7B%22elementName%22%3A%22form%22%2C%22className%22%3A%22relative%22%7D" onSubmit={handleSubmit} className="relative">
        <input data-component-id="src/components/common/SearchBar.tsx:97:8" data-component-path="src/components/common/SearchBar.tsx" data-component-line="97" data-component-end-line="108" data-component-file="SearchBar.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3Aquery%5D%22%2C%22className%22%3A%22w-full%20h-12%20pl-12%20pr-12%20bg-muted%20border%20border-border%20rounded-lg%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20font-body%20text-base%20text-foreground%20placeholder%3Atext-muted-foreground%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20transition-smooth%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-ring%20focus%3Aborder-transparent%22%7D"
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className="w-full h-12 pl-12 pr-12 bg-muted border border-border rounded-lg
                   font-body text-base text-foreground placeholder:text-muted-foreground
                   transition-smooth focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
        />
        <Icon data-component-id="src/components/common/SearchBar.tsx:109:8" data-component-path="src/components/common/SearchBar.tsx" data-component-line="109" data-component-end-line="113" data-component-file="SearchBar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22absolute%20left-4%20top-1%2F2%20-translate-y-1%2F2%20text-muted-foreground%20pointer-events-none%22%7D"
          name="MagnifyingGlassIcon"
          size={20}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
        />
        {query && (
          <button data-component-id="src/components/common/SearchBar.tsx:115:10" data-component-path="src/components/common/SearchBar.tsx" data-component-line="115" data-component-end-line="123" data-component-file="SearchBar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22absolute%20right-4%20top-1%2F2%20-translate-y-1%2F2%20text-muted-foreground%20%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20hover%3Atext-foreground%20transition-smooth%22%7D"
            type="button"
            onClick={handleClear}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground 
                     hover:text-foreground transition-smooth"
            aria-label="Clear search"
          >
            <Icon data-component-id="src/components/common/SearchBar.tsx:122:12" data-component-path="src/components/common/SearchBar.tsx" data-component-line="122" data-component-end-line="122" data-component-file="SearchBar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22XMarkIcon%22%7D" name="XMarkIcon" size={20} />
          </button>
        )}
      </form>

      {/* Search Suggestions */}
      {isFocused && suggestions.length > 0 && (
        <div data-component-id="src/components/common/SearchBar.tsx:129:8" data-component-path="src/components/common/SearchBar.tsx" data-component-line="129" data-component-end-line="156" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-full%20left-0%20right-0%20mt-2%20bg-card%20border%20border-border%20%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20rounded-lg%20shadow-lg%20overflow-hidden%20z-200%22%7D" className="absolute top-full left-0 right-0 mt-2 bg-card border border-border 
                      rounded-lg shadow-lg overflow-hidden z-200">
          <div data-component-id="src/components/common/SearchBar.tsx:131:10" data-component-path="src/components/common/SearchBar.tsx" data-component-line="131" data-component-end-line="155" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22py-2%22%7D" className="py-2">
            {suggestions.map((suggestion) => (
              <button data-component-id="src/components/common/SearchBar.tsx:133:14" data-component-path="src/components/common/SearchBar.tsx" data-component-line="133" data-component-end-line="153" data-component-file="SearchBar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20flex%20items-center%20justify-between%20px-4%20py-3%20%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20text-left%20hover%3Abg-muted%20transition-smooth%22%7D"
                key={suggestion.id}
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full flex items-center justify-between px-4 py-3 
                         text-left hover:bg-muted transition-smooth"
              >
                <div data-component-id="src/components/common/SearchBar.tsx:139:16" data-component-path="src/components/common/SearchBar.tsx" data-component-line="139" data-component-end-line="151" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-3%22%7D" className="flex items-center space-x-3">
                  <Icon data-component-id="src/components/common/SearchBar.tsx:140:18" data-component-path="src/components/common/SearchBar.tsx" data-component-line="140" data-component-end-line="140" data-component-file="SearchBar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22text-muted-foreground%22%7D" name="MagnifyingGlassIcon" size={16} className="text-muted-foreground" />
                  <div data-component-id="src/components/common/SearchBar.tsx:141:18" data-component-path="src/components/common/SearchBar.tsx" data-component-line="141" data-component-end-line="150" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                    <div data-component-id="src/components/common/SearchBar.tsx:142:20" data-component-path="src/components/common/SearchBar.tsx" data-component-line="142" data-component-end-line="144" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22font-body%20text-sm%20text-foreground%22%7D" className="font-body text-sm text-foreground">
                      {suggestion.text}
                    </div>
                    {suggestion.category && (
                      <div data-component-id="src/components/common/SearchBar.tsx:146:22" data-component-path="src/components/common/SearchBar.tsx" data-component-line="146" data-component-end-line="148" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22font-caption%20text-xs%20text-muted-foreground%20mt-0.5%22%7D" className="font-caption text-xs text-muted-foreground mt-0.5">
                        {suggestion.category}
                      </div>
                    )}
                  </div>
                </div>
                <Icon data-component-id="src/components/common/SearchBar.tsx:152:16" data-component-path="src/components/common/SearchBar.tsx" data-component-line="152" data-component-end-line="152" data-component-file="SearchBar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowUpRightIcon%22%2C%22className%22%3A%22text-muted-foreground%22%7D" name="ArrowUpRightIcon" size={16} className="text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;